import MaterialIcons from '@expo/vector-icons/MaterialIcons';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  Modal,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  useWindowDimensions,
  View,
} from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';

import { Fonts } from '@/constants/theme';
import {
  createAssignment,
  deleteAssignment,
  fetchAssignmentRowsForSchool,
  mapAssignmentsForSchool,
  updateAssignmentTeacher,
  type AssignmentListItem,
} from '@/lib/assignments-admin';
import { fetchClassesForSchool, type ClassListItem } from '@/lib/classes-admin';
import { fetchSubjectsForSchool, type SubjectListItem } from '@/lib/subjects-admin';
import { fetchTeachersForSchool, type TeacherListItem } from '@/lib/teacher-admin';
import { useAuth } from '@/providers/auth-provider';

type PickerType = 'class' | 'subject' | 'teacher' | null;

export default function AdminAssignmentsScreen() {
  const { profile } = useAuth();
  const insets = useSafeAreaInsets();
  const { width } = useWindowDimensions();
  const isDesktop = width >= 960;
  const [assignments, setAssignments] = useState<AssignmentListItem[]>([]);
  const [classes, setClasses] = useState<ClassListItem[]>([]);
  const [subjects, setSubjects] = useState<SubjectListItem[]>([]);
  const [teachers, setTeachers] = useState<TeacherListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [pickerType, setPickerType] = useState<PickerType>(null);
  const [selectedClassId, setSelectedClassId] = useState('');
  const [selectedSubjectId, setSelectedSubjectId] = useState('');
  const [selectedTeacherId, setSelectedTeacherId] = useState('');
  const [editingAssignment, setEditingAssignment] = useState<AssignmentListItem | null>(null);

  const loadData = useCallback(async () => {
    if (!profile?.school_id) {
      setLoading(false);
      return;
    }

    try {
      const [assignmentRows, classRows, subjectRows, teacherRows] = await Promise.all([
        fetchAssignmentRowsForSchool(profile.school_id),
        fetchClassesForSchool(profile.school_id),
        fetchSubjectsForSchool(profile.school_id),
        fetchTeachersForSchool(profile.school_id),
      ]);

      setAssignments(
        mapAssignmentsForSchool({
          assignments: assignmentRows,
          classes: classRows,
          subjects: subjectRows,
          teachers: teacherRows,
        })
      );
      setClasses(classRows);
      setSubjects(subjectRows);
      setTeachers(teacherRows.filter((teacher) => teacher.is_active));
    } catch (error) {
      Alert.alert('Unable to load assignments', getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [profile?.school_id]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const assignmentStats = useMemo(
    () => [
      { label: 'Total', value: String(assignments.length) },
      { label: 'Classes', value: String(classes.length) },
      { label: 'Teachers', value: String(teachers.length) },
    ],
    [assignments.length, classes.length, teachers.length]
  );

  const unassignedClasses = useMemo(
    () => classes.filter((item) => !assignments.some((assignment) => assignment.class_id === item.id)),
    [assignments, classes]
  );

  const teachersInUse = useMemo(
    () => new Set(assignments.map((assignment) => assignment.teacher_id)).size,
    [assignments]
  );

  const selectedClass = useMemo(
    () => classes.find((item) => item.id === selectedClassId) ?? null,
    [classes, selectedClassId]
  );
  const selectedSubject = useMemo(
    () => subjects.find((item) => item.id === selectedSubjectId) ?? null,
    [selectedSubjectId, subjects]
  );
  const selectedTeacher = useMemo(
    () => teachers.find((item) => item.id === selectedTeacherId) ?? null,
    [selectedTeacherId, teachers]
  );

  const resetForm = () => {
    setSelectedClassId('');
    setSelectedSubjectId('');
    setSelectedTeacherId('');
  };

  const openCreateModal = () => {
    setEditingAssignment(null);
    resetForm();
    setModalVisible(true);
  };

  const openTransferModal = (assignment: AssignmentListItem) => {
    setPickerType(null);
    setEditingAssignment(assignment);
    setSelectedClassId(assignment.class_id);
    setSelectedSubjectId(assignment.subject_id);
    setSelectedTeacherId(assignment.teacher_id);
    setModalVisible(true);
  };

  const closeModal = () => {
    setModalVisible(false);
    setPickerType(null);
    setEditingAssignment(null);
    resetForm();
  };

  const handleSubmit = async () => {
    if (!profile?.school_id) {
      Alert.alert('School missing', 'Your account is missing school access.');
      return;
    }

    if (!selectedClassId || !selectedSubjectId || !selectedTeacherId) {
      Alert.alert('Missing details', 'Select a class, subject, and teacher.');
      return;
    }

    setSubmitting(true);

    try {
      const assignmentBeingEdited = editingAssignment;

      if (assignmentBeingEdited) {
        await updateAssignmentTeacher({
          assignmentId: assignmentBeingEdited.id,
          schoolId: profile.school_id,
          teacherId: selectedTeacherId,
        });
      } else {
        await createAssignment({
          classId: selectedClassId,
          schoolId: profile.school_id,
          subjectId: selectedSubjectId,
          teacherId: selectedTeacherId,
        });
      }

      closeModal();
      await loadData();
      Alert.alert(
        assignmentBeingEdited ? 'Teacher replaced' : 'Assignment added',
        assignmentBeingEdited
          ? 'The subject is now assigned to the selected teacher.'
          : 'The link was created successfully.'
      );
    } catch (error) {
      Alert.alert(editingAssignment ? 'Replace failed' : 'Assignment failed', getErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemove = (id: string) => {
    if (!profile?.school_id) {
      Alert.alert('School missing', 'Your account is missing school access.');
      return;
    }

    Alert.alert('Remove assignment', 'This will unlink the teacher from the class and subject.', [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Remove',
        style: 'destructive',
        onPress: async () => {
          try {
            await deleteAssignment({ assignmentId: id, schoolId: profile.school_id });
            await loadData();
          } catch (error) {
            Alert.alert('Remove failed', getErrorMessage(error));
          }
        },
      },
    ]);
  };

  const pickerTitle = pickerType
    ? pickerType.charAt(0).toUpperCase() + pickerType.slice(1)
    : '';

  const pickerSheet = (
    isDesktop ? (
      <Modal
        animationType="fade"
        transparent
        visible={pickerType !== null}
        onRequestClose={() => setPickerType(null)}>
        <View style={styles.desktopPickerBackdrop}>
          <Pressable style={StyleSheet.absoluteFill} onPress={() => setPickerType(null)} />
          <View style={styles.desktopPickerCard}>
            <View style={styles.pickerHeader}>
              <Text style={styles.pickerTitle}>Select {pickerTitle.toLowerCase()}</Text>
              <Pressable onPress={() => setPickerType(null)} style={styles.desktopPickerClose}>
                <MaterialIcons name="close" size={24} color="#173D22" />
              </Pressable>
            </View>

            <ScrollView contentContainerStyle={styles.pickerList}>
              {pickerType === 'class' &&
                classes.map((item) => {
                  const active = selectedClassId === item.id;
                  return (
                    <Pressable
                      key={item.id}
                      onPress={() => {
                        setSelectedClassId(item.id);
                        setPickerType(null);
                      }}
                      style={[styles.pickerItem, active && styles.pickerItemActive]}>
                      <View>
                        <Text style={[styles.pickerName, active && styles.pickerNameActive]}>
                          {getClassLabel(item)}
                        </Text>
                        <Text style={styles.pickerMeta}>{item.academic_year}</Text>
                      </View>
                      {active && <MaterialIcons name="check-circle" size={20} color="#1B5E20" />}
                    </Pressable>
                  );
                })}

              {pickerType === 'subject' &&
                subjects.map((item) => {
                  const active = selectedSubjectId === item.id;
                  return (
                    <Pressable
                      key={item.id}
                      onPress={() => {
                        setSelectedSubjectId(item.id);
                        setPickerType(null);
                      }}
                      style={[styles.pickerItem, active && styles.pickerItemActive]}>
                      <View>
                        <Text style={[styles.pickerName, active && styles.pickerNameActive]}>
                          {getSubjectLabel(item)}
                        </Text>
                        <Text style={styles.pickerMeta}>{item.code || 'No code'}</Text>
                      </View>
                      {active && <MaterialIcons name="check-circle" size={20} color="#1B5E20" />}
                    </Pressable>
                  );
                })}

              {pickerType === 'teacher' &&
                teachers.map((item) => {
                  const active = selectedTeacherId === item.id;
                  return (
                    <Pressable
                      key={item.id}
                      onPress={() => {
                        setSelectedTeacherId(item.id);
                        setPickerType(null);
                      }}
                      style={[styles.pickerItem, active && styles.pickerItemActive]}>
                      <View>
                        <Text style={[styles.pickerName, active && styles.pickerNameActive]}>
                          {item.full_name || 'Teacher'}
                        </Text>
                        <Text style={styles.pickerMeta}>{item.email}</Text>
                      </View>
                      {active && <MaterialIcons name="check-circle" size={20} color="#1B5E20" />}
                    </Pressable>
                  );
                })}
            </ScrollView>
          </View>
        </View>
      </Modal>
    ) : null
  );

  const desktopModalSheet = (
    <Modal animationType="fade" transparent visible={modalVisible} onRequestClose={closeModal}>
      <View style={styles.desktopModalBackdrop}>
        <Pressable style={StyleSheet.absoluteFill} onPress={closeModal} />
        <View style={styles.desktopModalKeyboard}>
          <View style={styles.desktopModalShell}>
            <View style={styles.desktopModalCard}>
              <ScrollView
                contentContainerStyle={styles.desktopModalScroll}
                keyboardShouldPersistTaps="handled"
                showsVerticalScrollIndicator={false}>
                <View style={styles.desktopModalHeader}>
                  <View>
                    <Text style={styles.desktopModalHeaderLabel}>
                      {editingAssignment ? 'Replace teacher' : 'New assignment'}
                    </Text>
                    <Text style={styles.desktopModalHeaderValue}>
                      {editingAssignment ? 'Transfer subject ownership' : 'Connect teacher to class'}
                    </Text>
                    <Text style={styles.desktopModalHeaderSubtext}>
                      {editingAssignment
                        ? 'Select the new active teacher for this class and subject.'
                        : 'Choose a class, subject, and active teacher.'}
                    </Text>
                  </View>
                  <Pressable onPress={closeModal} style={styles.desktopModalClose}>
                    <MaterialIcons name="close" size={18} color="#163828" />
                  </Pressable>
                </View>

                <View style={styles.desktopModalForm}>
                  <PickerField
                    label="CLASS"
                    value={selectedClass ? getClassLabel(selectedClass) : 'Select class'}
                    onPress={() => setPickerType('class')}
                    icon="domain"
                    disabled={Boolean(editingAssignment)}
                  />
                  <PickerField
                    label="SUBJECT"
                    value={selectedSubject ? getSubjectLabel(selectedSubject) : 'Select subject'}
                    onPress={() => setPickerType('subject')}
                    icon="library-books"
                    disabled={Boolean(editingAssignment)}
                  />
                  <PickerField
                    label="TEACHER"
                    value={selectedTeacher ? selectedTeacher.full_name || 'Teacher' : 'Select teacher'}
                    onPress={() => setPickerType('teacher')}
                    icon="badge"
                  />
                </View>

                <View style={styles.desktopModalActions}>
                  <Pressable onPress={closeModal} style={styles.desktopModalCancel}>
                    <Text style={styles.desktopModalCancelText}>Cancel</Text>
                  </Pressable>
                  <Pressable onPress={handleSubmit} style={styles.desktopModalSave}>
                    {submitting ? (
                      <ActivityIndicator color="#FFFFFF" />
                    ) : (
                      <Text style={styles.desktopModalSaveText}>
                        {editingAssignment ? 'Replace teacher' : 'Save assignment'}
                      </Text>
                    )}
                  </Pressable>
                </View>
              </ScrollView>
            </View>
          </View>
        </View>
      </View>
    </Modal>
  );

  if (isDesktop) {
    return (
      <>
        <View style={styles.desktopPage}>
          <ScrollView
            contentContainerStyle={styles.desktopScroll}
            showsVerticalScrollIndicator={false}
            style={styles.desktopScrollView}>
            <View style={styles.desktopHero}>
              <View style={styles.desktopHeroTop}>
                <View style={styles.desktopHeroCopy}>
                  <Text style={styles.desktopEyebrow}>Admin / Assignments</Text>
                  <Text style={styles.desktopTitle}>Assignment matrix</Text>
                  <Text style={styles.desktopSubtitle}>
                    Connect teachers, classes, and subjects from a single workspace.
                  </Text>
                </View>

                <Pressable onPress={openCreateModal} style={styles.desktopHeaderBtn}>
                  <MaterialIcons name="add" size={20} color="#F4EFE6" />
                  <Text style={styles.desktopHeaderBtnText}>Add assignment</Text>
                </Pressable>
              </View>

              <View style={styles.desktopHeroPanel}>
                <View style={styles.desktopHeroPanelMain}>
                  <Text style={styles.desktopHeroDate}>Workspace overview</Text>
                  <Text style={styles.desktopHeroHeadline}>Teacher coverage and class links.</Text>
                  <Text style={styles.desktopHeroBody}>
                    Use the desktop view to scan linked pairs, spot unassigned classes, and add or
                    remove connections quickly.
                  </Text>
                </View>

                <View style={styles.desktopStatsGrid}>
                  {assignmentStats.map((item, index) => (
                    <View
                      key={item.label}
                      style={[styles.desktopStatCard, index > 0 ? styles.desktopStatDivider : null]}>
                      <Text style={styles.desktopStatValue}>{item.value}</Text>
                      <Text style={styles.desktopStatLabel}>{item.label}</Text>
                    </View>
                  ))}
                </View>
              </View>
            </View>

            <View style={styles.desktopGrid}>
              <View style={styles.desktopColumnLarge}>
                <View style={styles.desktopSection}>
                  <View style={styles.sectionHeadDesktop}>
                    <View>
                      <Text style={styles.sectionTitleDesktop}>All assignments</Text>
                      <Text style={styles.sectionCaptionDesktop}>
                        Remove or review teacher links from the roster.
                      </Text>
                    </View>
                    <Pressable onPress={openCreateModal}>
                      <Text style={styles.linkTextDesktop}>Add assignment</Text>
                    </Pressable>
                  </View>

                  {loading ? (
                    <View style={styles.desktopLoadingWrap}>
                      <ActivityIndicator color="#163828" />
                    </View>
                  ) : assignments.length === 0 ? (
                    <View style={styles.desktopEmptyState}>
                      <View style={styles.desktopEmptyIcon}>
                        <MaterialIcons name="link" size={26} color="#163828" />
                      </View>
                      <Text style={styles.desktopEmptyTitle}>No assignments yet</Text>
                      <Text style={styles.desktopEmptyText}>
                        Create the first link to connect a teacher with a class and subject.
                      </Text>
                      <Pressable onPress={openCreateModal} style={styles.desktopEmptyButton}>
                        <Text style={styles.desktopEmptyButtonText}>Create assignment</Text>
                      </Pressable>
                    </View>
                  ) : (
                    <View style={styles.desktopListBlock}>
                      {assignments.map((item, index) => (
                        <View
                          key={item.id}
                          style={[
                            styles.desktopListRow,
                            index < assignments.length - 1 ? styles.desktopListDivider : null,
                          ]}>
                          <View style={styles.desktopRowIndex}>
                            <Text style={styles.desktopRowIndexText}>
                              {item.class_label.slice(0, 1).toUpperCase()}
                            </Text>
                          </View>

                          <View style={styles.desktopRowCopy}>
                            <Text style={styles.desktopRowTitle} numberOfLines={1}>
                              {item.class_label}
                            </Text>
                            <Text style={styles.desktopRowMeta} numberOfLines={1}>
                              {item.subject_label}
                            </Text>
                          </View>

                          <View style={styles.desktopRowRight}>
                            <View style={styles.desktopRowTag}>
                              <Text style={styles.desktopRowTagText}>Linked</Text>
                            </View>
                            <Text style={styles.desktopRowCount}>{item.teacher_label}</Text>
                          </View>

                          <Pressable
                            onPress={() => openTransferModal(item)}
                            style={styles.desktopTransferBtn}>
                            <MaterialIcons name="swap-horiz" size={18} color="#1B5E20" />
                          </Pressable>
                          <Pressable onPress={() => handleRemove(item.id)} style={styles.desktopDeleteBtn}>
                            <MaterialIcons name="delete-outline" size={18} color="#D32F2F" />
                          </Pressable>
                        </View>
                      ))}
                    </View>
                  )}
                </View>
              </View>

              <View style={styles.desktopColumnSide}>
                <View style={styles.desktopSection}>
                  <View style={styles.sectionHeadDesktop}>
                    <View>
                      <Text style={styles.sectionTitleDesktop}>Coverage</Text>
                      <Text style={styles.sectionCaptionDesktop}>
                        Teacher usage and missing class links.
                      </Text>
                    </View>
                  </View>

                  <View style={styles.desktopSnapshotCard}>
                    <Text style={styles.snapshotLabel}>Active teachers</Text>
                    <Text style={styles.desktopSnapshotTitle}>{String(teachersInUse)}</Text>
                    <Text style={styles.desktopSnapshotMeta}>
                      {teachers.length} active teacher accounts available for assignment.
                    </Text>
                  </View>
                </View>

                <View style={styles.desktopSection}>
                  <View style={styles.sectionHeadDesktop}>
                    <View>
                      <Text style={styles.sectionTitleDesktop}>Unassigned classes</Text>
                      <Text style={styles.sectionCaptionDesktop}>
                        Classes that still need a teacher and subject.
                      </Text>
                    </View>
                  </View>

                  {loading ? (
                    <View style={styles.desktopLoadingWrap}>
                      <ActivityIndicator color="#163828" />
                    </View>
                  ) : unassignedClasses.length === 0 ? (
                    <View style={styles.desktopEmptyState}>
                      <Text style={styles.desktopEmptyTitle}>All classes linked</Text>
                      <Text style={styles.desktopEmptyText}>
                        Every class currently has at least one assignment.
                      </Text>
                    </View>
                  ) : (
                    <View style={styles.desktopListBlock}>
                      {unassignedClasses.map((item, index) => (
                        <View
                          key={item.id}
                          style={[
                            styles.desktopListRow,
                            index < unassignedClasses.length - 1 ? styles.desktopListDivider : null,
                          ]}>
                          <View style={styles.desktopRowIndex}>
                            <Text style={styles.desktopRowIndexText}>
                              {item.name.slice(0, 1).toUpperCase()}
                            </Text>
                          </View>

                          <View style={styles.desktopRowCopy}>
                            <Text style={styles.desktopRowTitle} numberOfLines={1}>
                              {getClassLabel(item)}
                            </Text>
                            <Text style={styles.desktopRowMeta} numberOfLines={1}>
                              {item.academic_year}
                            </Text>
                          </View>

                          <View style={styles.desktopRowRight}>
                            <View style={[styles.desktopRowTag, { backgroundColor: '#F5F6FA' }]}>
                              <Text style={[styles.desktopRowTagText, { color: '#8B93A0' }]}>
                                Open
                              </Text>
                            </View>
                            <Text style={styles.desktopRowCount}>Needs link</Text>
                          </View>
                        </View>
                      ))}
                    </View>
                  )}
                </View>
              </View>
            </View>
          </ScrollView>
        </View>

        {desktopModalSheet}
        {pickerSheet}
      </>
    );
  }

  return (
    <View style={styles.container}>
      <SafeAreaView style={styles.safeArea}>
        <ScrollView
          contentContainerStyle={[
            styles.scrollContent,
            { paddingBottom: Math.max(insets.bottom, 20) + 88 },
          ]}
          showsVerticalScrollIndicator={false}>
          <View style={styles.heroShell}>
            <View style={styles.heroPanel}>
              <View style={styles.heroHeaderRow}>
                <Text style={styles.heroKicker}>Admin / Assignments</Text>
                <Pressable
                  onPress={openCreateModal}
                  style={({ pressed }) => [styles.heroButton, pressed && styles.pressed]}>
                  <MaterialIcons name="add" size={18} color="#163828" />
                  <Text style={styles.heroButtonText}>Add assignment</Text>
                </Pressable>
              </View>

              <View style={styles.heroBody}>
                <Text style={styles.heroTitle}>Connect teachers to classes and subjects.</Text>
                <Text style={styles.heroSubtitle}>
                  Keep the school links clear so teaching coverage stays easy to scan.
                </Text>
              </View>

              <View style={styles.heroStatsRow}>
                {assignmentStats.map((item, index) => (
                  <View
                    key={item.label}
                    style={[styles.heroStat, index > 0 ? styles.heroStatDivider : null]}>
                    <Text style={styles.heroStatValue}>{item.value}</Text>
                    <Text style={styles.heroStatLabel}>{item.label}</Text>
                  </View>
                ))}
              </View>
            </View>
          </View>

          <View style={styles.surface}>
            <View style={styles.sectionBlock}>
              <View style={styles.sectionHead}>
                <Text style={styles.sectionTitle}>All Assignments</Text>
                <Pressable onPress={openCreateModal}>
                  <Text style={styles.linkText}>Add assignment</Text>
                </Pressable>
              </View>

              {loading ? (
                <View style={styles.loadingWrap}>
                  <ActivityIndicator color="#163828" />
                </View>
              ) : assignments.length === 0 ? (
                <View style={styles.emptyState}>
                  <View style={styles.emptyIcon}>
                    <MaterialIcons name="link" size={28} color="#163828" />
                  </View>
                  <Text style={styles.emptyTitle}>No assignments yet</Text>
                  <Text style={styles.emptyText}>
                    Create the first link to connect a teacher with a class and subject.
                  </Text>
                  <Pressable onPress={openCreateModal} style={styles.emptyButton}>
                    <Text style={styles.emptyButtonText}>Create assignment</Text>
                  </Pressable>
                </View>
              ) : (
                <View style={styles.listBlock}>
                  {assignments.map((item, index) => (
                    <Pressable
                      key={item.id}
                      style={[
                        styles.listRow,
                        index < assignments.length - 1 ? styles.listDivider : null,
                      ]}>
                      <View style={styles.rowIndex}>
                        <Text style={styles.rowIndexText}>
                          {item.class_label.slice(0, 1).toUpperCase()}
                        </Text>
                      </View>

                      <View style={styles.rowCopy}>
                        <Text style={styles.rowTitle} numberOfLines={1}>
                          {item.class_label}
                        </Text>
                        <Text style={styles.rowMeta} numberOfLines={1}>
                          {item.subject_label}
                        </Text>
                      </View>

                      <View style={styles.rowRight}>
                        <View style={styles.rowTag}>
                          <Text style={styles.rowTagText}>Linked</Text>
                        </View>
                        <Text style={styles.rowCount}>{item.teacher_label}</Text>
                        <Pressable onPress={() => openTransferModal(item)} style={styles.transferBtn}>
                          <MaterialIcons name="swap-horiz" size={18} color="#1B5E20" />
                        </Pressable>
                        <Pressable onPress={() => handleRemove(item.id)} style={styles.deleteBtn}>
                          <MaterialIcons name="delete-outline" size={18} color="#D32F2F" />
                        </Pressable>
                      </View>
                    </Pressable>
                  ))}
                </View>
              )}
            </View>
          </View>
        </ScrollView>
      </SafeAreaView>

      <Modal animationType="slide" transparent visible={modalVisible} onRequestClose={closeModal}>
        <View style={styles.modalBackdrop}>
          <View style={styles.modalCard}>
            <View style={styles.modalHandle} />
            <Text style={styles.modalTitle}>
              {editingAssignment ? 'Replace teacher' : 'New assignment'}
            </Text>
            <Text style={styles.modalSubtitle}>
              {editingAssignment
                ? 'Select the new active teacher for this class and subject.'
                : 'Choose a class, subject, and active teacher.'}
            </Text>

            <PickerField
              label="CLASS"
              value={selectedClass ? getClassLabel(selectedClass) : 'Select class'}
              onPress={() => setPickerType((current) => (current === 'class' ? null : 'class'))}
              icon="domain"
              disabled={Boolean(editingAssignment)}
            />
            <PickerField
              label="SUBJECT"
              value={selectedSubject ? getSubjectLabel(selectedSubject) : 'Select subject'}
              onPress={() => setPickerType((current) => (current === 'subject' ? null : 'subject'))}
              icon="library-books"
              disabled={Boolean(editingAssignment)}
            />
            <PickerField
              label="TEACHER"
              value={selectedTeacher ? selectedTeacher.full_name || 'Teacher' : 'Select teacher'}
              onPress={() => setPickerType((current) => (current === 'teacher' ? null : 'teacher'))}
              icon="badge"
            />

            {!isDesktop && pickerType !== null && (
              <View style={styles.pickerInlinePanel}>
                <View style={styles.pickerHeader}>
                  <Text style={styles.pickerTitle}>Select {pickerTitle.toLowerCase()}</Text>
                  <Pressable onPress={() => setPickerType(null)}>
                    <MaterialIcons name="close" size={24} color="#173D22" />
                  </Pressable>
                </View>

                <ScrollView
                  contentContainerStyle={styles.pickerList}
                  keyboardShouldPersistTaps="handled"
                  nestedScrollEnabled>
                  {pickerType === 'class' &&
                    classes.map((item) => {
                      const active = selectedClassId === item.id;
                      return (
                        <Pressable
                          key={item.id}
                          onPress={() => {
                            setSelectedClassId(item.id);
                            setPickerType(null);
                          }}
                          style={[styles.pickerItem, active && styles.pickerItemActive]}>
                          <View>
                            <Text style={[styles.pickerName, active && styles.pickerNameActive]}>
                              {getClassLabel(item)}
                            </Text>
                            <Text style={styles.pickerMeta}>{item.academic_year}</Text>
                          </View>
                          {active && <MaterialIcons name="check-circle" size={20} color="#1B5E20" />}
                        </Pressable>
                      );
                    })}

                  {pickerType === 'subject' &&
                    subjects.map((item) => {
                      const active = selectedSubjectId === item.id;
                      return (
                        <Pressable
                          key={item.id}
                          onPress={() => {
                            setSelectedSubjectId(item.id);
                            setPickerType(null);
                          }}
                          style={[styles.pickerItem, active && styles.pickerItemActive]}>
                          <View>
                            <Text style={[styles.pickerName, active && styles.pickerNameActive]}>
                              {getSubjectLabel(item)}
                            </Text>
                            <Text style={styles.pickerMeta}>{item.code || 'No code'}</Text>
                          </View>
                          {active && <MaterialIcons name="check-circle" size={20} color="#1B5E20" />}
                        </Pressable>
                      );
                    })}

                  {pickerType === 'teacher' &&
                    teachers.map((item) => {
                      const active = selectedTeacherId === item.id;
                      return (
                        <Pressable
                          key={item.id}
                          onPress={() => {
                            setSelectedTeacherId(item.id);
                            setPickerType(null);
                          }}
                          style={[styles.pickerItem, active && styles.pickerItemActive]}>
                          <View>
                            <Text style={[styles.pickerName, active && styles.pickerNameActive]}>
                              {item.full_name || 'Teacher'}
                            </Text>
                            <Text style={styles.pickerMeta}>{item.email}</Text>
                          </View>
                          {active && <MaterialIcons name="check-circle" size={20} color="#1B5E20" />}
                        </Pressable>
                      );
                    })}
                </ScrollView>
              </View>
            )}

            <View style={styles.modalActions}>
              <Pressable onPress={closeModal} style={styles.cancelBtn}>
                <Text style={styles.cancelBtnText}>Cancel</Text>
              </Pressable>
              <Pressable onPress={handleSubmit} style={styles.saveBtn}>
                {submitting ? (
                  <ActivityIndicator color="#FFFFFF" />
                ) : (
                  <Text style={styles.saveBtnText}>
                    {editingAssignment ? 'Replace teacher' : 'Save assignment'}
                  </Text>
                )}
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>
      {pickerSheet}
    </View>
  );
}

function PickerField({
  disabled = false,
  icon,
  label,
  onPress,
  value,
}: {
  disabled?: boolean;
  icon: keyof typeof MaterialIcons.glyphMap;
  label: string;
  onPress: () => void;
  value: string;
}) {
  const isPlaceholder = value.startsWith('Select');

  return (
    <Pressable disabled={disabled} onPress={onPress} style={styles.pickerField}>
      <Text style={styles.fieldLabel}>{label}</Text>
      <View style={[styles.pickerFieldBox, disabled && styles.pickerFieldDisabled]}>
        <View style={styles.pickerFieldLeft}>
          <MaterialIcons name={icon} size={18} color="#1B5E20" />
          <Text style={[styles.pickerFieldValue, isPlaceholder && styles.pickerFieldPlaceholder]}>
            {value}
          </Text>
        </View>
        <MaterialIcons name={disabled ? 'lock' : 'unfold-more'} size={20} color="#C7D5C9" />
      </View>
    </Pressable>
  );
}

function getClassLabel(item: ClassListItem) {
  return item.section?.trim() ? `${item.name} - ${item.section}` : item.name;
}

function getSubjectLabel(item: SubjectListItem) {
  return item.code?.trim() ? `${item.name} (${item.code})` : item.name;
}

function getErrorMessage(error: unknown) {
  if (error instanceof Error) return error.message;
  return 'Something went wrong.';
}

const styles = StyleSheet.create({
  desktopPage: {
    flex: 1,
    backgroundColor: '#F4EFE6',
  },
  desktopScrollView: {
    flex: 1,
  },
  desktopScroll: {
    padding: 28,
    gap: 24,
  },
  desktopHero: {
    gap: 18,
  },
  desktopHeroTop: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 20,
  },
  desktopHeroCopy: {
    gap: 6,
    flex: 1,
    minWidth: 0,
  },
  desktopEyebrow: {
    color: '#6F7D71',
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 1.2,
    fontWeight: '800',
  },
  desktopTitle: {
    color: '#163828',
    fontSize: 34,
    lineHeight: 40,
    fontFamily: Fonts.rounded,
    fontWeight: '900',
  },
  desktopSubtitle: {
    color: '#6F7D71',
    fontSize: 14,
    lineHeight: 20,
    maxWidth: 640,
  },
  desktopHeaderBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    backgroundColor: '#163828',
    borderRadius: 18,
    paddingHorizontal: 14,
    paddingVertical: 12,
    minWidth: 190,
  },
  desktopHeaderBtnText: {
    color: '#F7F4EE',
    fontSize: 13,
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  desktopHeroPanel: {
    backgroundColor: '#103427',
    borderRadius: 28,
    padding: 22,
    gap: 18,
    borderWidth: 1,
    borderColor: 'rgba(244, 239, 230, 0.09)',
  },
  desktopHeroPanelMain: {
    gap: 8,
    maxWidth: 560,
  },
  desktopHeroDate: {
    color: '#BBD0C2',
    fontSize: 11,
    fontWeight: '800',
    textTransform: 'uppercase',
    letterSpacing: 1.1,
  },
  desktopHeroHeadline: {
    color: '#F7F4EE',
    fontSize: 28,
    lineHeight: 34,
    fontFamily: Fonts.rounded,
    fontWeight: '900',
  },
  desktopHeroBody: {
    color: '#C4D3C9',
    fontSize: 14,
    lineHeight: 21,
    maxWidth: 620,
  },
  desktopStatsGrid: {
    flexDirection: 'row',
    gap: 12,
  },
  desktopStatCard: {
    flex: 1,
    borderRadius: 18,
    backgroundColor: 'rgba(244, 239, 230, 0.08)',
    borderWidth: 1,
    borderColor: 'rgba(244, 239, 230, 0.1)',
    padding: 16,
  },
  desktopStatDivider: {
    marginLeft: 0,
  },
  desktopStatValue: {
    color: '#F7F4EE',
    fontSize: 28,
    lineHeight: 32,
    fontFamily: Fonts.rounded,
    fontWeight: '900',
  },
  desktopStatLabel: {
    color: '#9EB5A7',
    fontSize: 12,
    marginTop: 4,
    fontWeight: '700',
  },
  desktopGrid: {
    flexDirection: 'row',
    gap: 20,
    alignItems: 'flex-start',
  },
  desktopColumnLarge: {
    flex: 1.35,
    gap: 20,
    minWidth: 0,
  },
  desktopColumnSide: {
    flex: 0.95,
    gap: 20,
    minWidth: 0,
  },
  desktopSection: {
    gap: 14,
  },
  sectionHeadDesktop: {
    gap: 4,
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
  },
  sectionTitleDesktop: {
    color: '#163828',
    fontSize: 20,
    lineHeight: 24,
    fontFamily: Fonts.rounded,
    fontWeight: '800',
  },
  sectionCaptionDesktop: {
    color: '#6F7D71',
    fontSize: 13,
    lineHeight: 18,
  },
  linkTextDesktop: {
    color: '#47624F',
    fontSize: 13,
    fontWeight: '700',
  },
  desktopListBlock: {
    backgroundColor: '#FFFFFF',
    borderRadius: 22,
    overflow: 'hidden',
  },
  desktopListRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 14,
  },
  desktopListDivider: {
    borderBottomWidth: 1,
    borderBottomColor: '#EEF2EA',
  },
  desktopRowIndex: {
    width: 38,
    height: 38,
    borderRadius: 12,
    backgroundColor: '#F0F5F0',
    alignItems: 'center',
    justifyContent: 'center',
  },
  desktopRowIndexText: {
    color: '#163828',
    fontSize: 13,
    fontWeight: '900',
    fontFamily: Fonts.rounded,
  },
  desktopRowCopy: {
    flex: 1,
    gap: 2,
    minWidth: 0,
  },
  desktopRowTitle: {
    color: '#163828',
    fontSize: 15,
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  desktopRowMeta: {
    color: '#6F7D71',
    fontSize: 12,
    lineHeight: 16,
  },
  desktopRowRight: {
    alignItems: 'flex-end',
    gap: 4,
  },
  desktopRowTag: {
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 4,
    alignItems: 'center',
    justifyContent: 'center',
  },
  desktopRowTagText: {
    fontSize: 10,
    fontWeight: '800',
    letterSpacing: 0.8,
    textTransform: 'uppercase',
    fontFamily: Fonts.sans,
  },
  desktopRowCount: {
    color: '#6F7D71',
    fontSize: 11,
    fontWeight: '700',
  },
  desktopTransferBtn: {
    width: 38,
    height: 38,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F0F8F0',
    borderWidth: 1,
    borderColor: '#D7E8D7',
  },
  desktopDeleteBtn: {
    width: 38,
    height: 38,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFF5F5',
    borderWidth: 1,
    borderColor: '#F1D6D6',
  },
  desktopLoadingWrap: {
    backgroundColor: '#FFFFFF',
    borderRadius: 22,
    paddingVertical: 28,
    alignItems: 'center',
    justifyContent: 'center',
  },
  desktopEmptyState: {
    backgroundColor: '#FFFFFF',
    borderRadius: 22,
    padding: 18,
    alignItems: 'center',
    gap: 8,
  },
  desktopEmptyIcon: {
    width: 48,
    height: 48,
    borderRadius: 16,
    backgroundColor: '#EEF5EE',
    alignItems: 'center',
    justifyContent: 'center',
  },
  desktopEmptyTitle: {
    color: '#163828',
    fontSize: 15,
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  desktopEmptyText: {
    color: '#6F7D71',
    fontSize: 13,
    lineHeight: 19,
    textAlign: 'center',
  },
  desktopEmptyButton: {
    alignSelf: 'stretch',
    minHeight: 44,
    borderRadius: 14,
    backgroundColor: '#163828',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 4,
  },
  desktopEmptyButtonText: {
    color: '#FFFFFF',
    fontSize: 13,
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  desktopSnapshotCard: {
    backgroundColor: '#163828',
    borderRadius: 24,
    padding: 18,
    gap: 8,
  },
  snapshotLabel: {
    color: '#BBD0C2',
    fontSize: 11,
    fontWeight: '800',
    textTransform: 'uppercase',
    letterSpacing: 1.1,
  },
  desktopSnapshotTitle: {
    color: '#F7F4EE',
    fontSize: 28,
    lineHeight: 32,
    fontWeight: '900',
    fontFamily: Fonts.rounded,
  },
  desktopSnapshotMeta: {
    color: '#C7D5CB',
    fontSize: 13,
    lineHeight: 19,
  },
  desktopModalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(13, 22, 16, 0.62)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  desktopModalKeyboard: {
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  desktopModalShell: {
    width: '100%',
    maxWidth: 760,
    borderRadius: 30,
    overflow: 'hidden',
    backgroundColor: '#FFFFFF',
    shadowColor: '#0E2817',
    shadowOpacity: 0.22,
    shadowRadius: 28,
    shadowOffset: { width: 0, height: 18 },
    elevation: 24,
  },
  desktopModalCard: {
    backgroundColor: '#FDFBF6',
    paddingHorizontal: 28,
    paddingVertical: 24,
  },
  desktopModalScroll: {
    gap: 18,
    paddingBottom: 4,
  },
  desktopModalHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 16,
  },
  desktopModalHeaderLabel: {
    color: '#6F7D71',
    fontSize: 10,
    fontWeight: '800',
    letterSpacing: 1,
    textTransform: 'uppercase',
    fontFamily: Fonts.sans,
  },
  desktopModalHeaderValue: {
    color: '#163828',
    fontSize: 22,
    lineHeight: 28,
    fontWeight: '900',
    fontFamily: Fonts.rounded,
    marginTop: 2,
  },
  desktopModalHeaderSubtext: {
    color: '#6F7D71',
    fontSize: 13,
    lineHeight: 18,
    marginTop: 4,
    maxWidth: 520,
  },
  desktopModalClose: {
    width: 40,
    height: 40,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E7EEE7',
  },
  desktopModalForm: {
    gap: 16,
  },
  desktopModalActions: {
    flexDirection: 'row',
    gap: 12,
    paddingTop: 8,
  },
  desktopModalCancel: {
    flex: 1,
    minHeight: 52,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F5F7F5',
    borderWidth: 1,
    borderColor: '#E6ECE6',
  },
  desktopModalCancelText: {
    color: '#3C4A3E',
    fontSize: 13,
    fontWeight: '800',
  },
  desktopModalSave: {
    flex: 1.4,
    minHeight: 52,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#163828',
  },
  desktopModalSaveText: {
    color: '#FFFFFF',
    fontSize: 13,
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  desktopPickerBackdrop: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    backgroundColor: 'rgba(13, 22, 16, 0.42)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  desktopPickerCard: {
    width: '100%',
    maxWidth: 620,
    maxHeight: '80%',
    backgroundColor: '#FFFFFF',
    borderRadius: 28,
    padding: 20,
    gap: 14,
    shadowColor: '#0E2817',
    shadowOpacity: 0.18,
    shadowRadius: 24,
    shadowOffset: { width: 0, height: 16 },
    elevation: 20,
  },
  desktopPickerClose: {
    width: 36,
    height: 36,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F5F7F5',
  },
  pickerInlinePanel: {
    backgroundColor: '#FFFFFF',
    borderRadius: 22,
    padding: 16,
    gap: 14,
    borderWidth: 1,
    borderColor: '#E7EEE7',
  },
  container: {
    flex: 1,
    backgroundColor: '#F4EFE6',
  },
  safeArea: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: 18,
    paddingTop: 10,
    gap: 18,
  },
  heroShell: {
    gap: 18,
  },
  heroPanel: {
    backgroundColor: '#163828',
    borderRadius: 28,
    padding: 18,
    gap: 18,
  },
  heroHeaderRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  heroKicker: {
    color: '#B9CFBE',
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 1.2,
    textTransform: 'uppercase',
    fontFamily: Fonts.sans,
    flexShrink: 1,
  },
  heroButton: {
    alignSelf: 'flex-start',
    backgroundColor: '#F4EFE6',
    borderRadius: 16,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  heroButtonText: {
    color: '#163828',
    fontSize: 13,
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  heroBody: {
    gap: 8,
  },
  heroTitle: {
    color: '#FFFFFF',
    fontSize: 27,
    lineHeight: 33,
    fontWeight: '900',
    fontFamily: Fonts.rounded,
  },
  heroSubtitle: {
    color: '#D4E4D7',
    fontSize: 14,
    lineHeight: 20,
  },
  heroStatsRow: {
    flexDirection: 'row',
    borderRadius: 20,
    overflow: 'hidden',
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
  },
  heroStat: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: 14,
    paddingHorizontal: 10,
    gap: 3,
  },
  heroStatDivider: {
    borderLeftWidth: 1,
    borderLeftColor: 'rgba(255, 255, 255, 0.12)',
  },
  heroStatValue: {
    color: '#FFFFFF',
    fontSize: 20,
    fontWeight: '900',
    fontFamily: Fonts.rounded,
  },
  heroStatLabel: {
    color: '#B9CFBE',
    fontSize: 10,
    fontWeight: '800',
    letterSpacing: 1.1,
    textTransform: 'uppercase',
    fontFamily: Fonts.sans,
  },
  surface: {
    gap: 18,
  },
  sectionBlock: {
    gap: 12,
  },
  sectionHead: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  sectionTitle: {
    color: '#163828',
    fontSize: 18,
    fontWeight: '900',
    fontFamily: Fonts.rounded,
  },
  linkText: {
    color: '#6F7D71',
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 1.1,
    textTransform: 'uppercase',
    fontFamily: Fonts.sans,
  },
  listBlock: {
    backgroundColor: '#FFFFFF',
    borderRadius: 22,
    overflow: 'hidden',
  },
  listRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 14,
  },
  listDivider: {
    borderBottomWidth: 1,
    borderBottomColor: '#EEF2EA',
  },
  rowIndex: {
    width: 38,
    height: 38,
    borderRadius: 12,
    backgroundColor: '#EAF3FF',
    alignItems: 'center',
    justifyContent: 'center',
  },
  rowIndexText: {
    color: '#0F4C81',
    fontSize: 13,
    fontWeight: '900',
    fontFamily: Fonts.rounded,
  },
  rowCopy: {
    flex: 1,
    gap: 2,
  },
  rowTitle: {
    color: '#163828',
    fontSize: 15,
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  rowMeta: {
    color: '#6F7D71',
    fontSize: 12,
    lineHeight: 16,
  },
  rowRight: {
    alignItems: 'flex-end',
    gap: 4,
  },
  rowTag: {
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 4,
    backgroundColor: '#E8F5E9',
  },
  rowTagText: {
    color: '#1B5E20',
    fontSize: 10,
    fontWeight: '800',
    letterSpacing: 0.8,
    textTransform: 'uppercase',
    fontFamily: Fonts.sans,
  },
  rowCount: {
    color: '#6F7D71',
    fontSize: 11,
    fontWeight: '700',
  },
  transferBtn: {
    marginTop: 2,
    width: 28,
    height: 28,
    borderRadius: 9,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#EEF8EE',
  },
  deleteBtn: {
    marginTop: 2,
    width: 28,
    height: 28,
    borderRadius: 9,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFF1F1',
  },
  loadingWrap: {
    backgroundColor: '#FFFFFF',
    borderRadius: 22,
    paddingVertical: 28,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyState: {
    backgroundColor: '#FFFFFF',
    borderRadius: 22,
    padding: 18,
    alignItems: 'center',
    gap: 8,
  },
  emptyIcon: {
    width: 48,
    height: 48,
    borderRadius: 16,
    backgroundColor: '#EEF5EE',
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyTitle: {
    color: '#163828',
    fontSize: 15,
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  emptyText: {
    color: '#6F7D71',
    fontSize: 13,
    lineHeight: 19,
    textAlign: 'center',
  },
  emptyButton: {
    alignSelf: 'stretch',
    minHeight: 44,
    borderRadius: 14,
    backgroundColor: '#163828',
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyButtonText: {
    color: '#FFFFFF',
    fontSize: 13,
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.4)',
    justifyContent: 'flex-end',
  },
  modalCard: {
    borderTopLeftRadius: 30,
    borderTopRightRadius: 30,
    backgroundColor: '#FFFFFF',
    padding: 22,
    paddingBottom: 40,
    gap: 16,
  },
  modalHandle: {
    width: 36,
    height: 4,
    borderRadius: 2,
    backgroundColor: '#EDF2EE',
    alignSelf: 'center',
  },
  modalTitle: {
    color: '#163828',
    fontSize: 20,
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  modalSubtitle: {
    color: '#6A8570',
    fontSize: 13,
    lineHeight: 19,
    marginTop: -8,
  },
  field: {
    gap: 8,
  },
  fieldLabel: {
    color: '#163828',
    fontSize: 10,
    fontWeight: '800',
    letterSpacing: 1,
    fontFamily: Fonts.sans,
  },
  input: {
    minHeight: 52,
    borderRadius: 14,
    borderWidth: 1.5,
    borderColor: '#E4E8E4',
    backgroundColor: '#FAFBFA',
    paddingHorizontal: 14,
    color: '#1C1C1E',
    fontSize: 15,
  },
  modalActions: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 4,
  },
  cancelBtn: {
    flex: 1,
    minHeight: 52,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F5F7F5',
    borderWidth: 1,
    borderColor: '#E6ECE6',
  },
  cancelBtnText: {
    color: '#3C4A3E',
    fontSize: 12,
    fontWeight: '800',
  },
  saveBtn: {
    flex: 2,
    minHeight: 52,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#163828',
  },
  saveBtnText: {
    color: '#FFFFFF',
    fontSize: 13,
    fontWeight: '800',
  },
  pickerField: {
    gap: 8,
  },
  pickerFieldBox: {
    minHeight: 52,
    borderRadius: 14,
    borderWidth: 1.5,
    borderColor: '#E4E8E4',
    backgroundColor: '#FAFBFA',
    paddingHorizontal: 14,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  pickerFieldDisabled: {
    opacity: 0.72,
  },
  pickerFieldLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    flex: 1,
  },
  pickerFieldValue: {
    color: '#1C1C1E',
    fontSize: 15,
  },
  pickerFieldPlaceholder: {
    color: '#A8B9AA',
  },
  pickerOverlay: {
    position: 'absolute',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.35)',
    justifyContent: 'flex-end',
  },
  pickerCard: {
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: 26,
    borderTopRightRadius: 26,
    padding: 18,
    gap: 14,
    maxHeight: '72%',
  },
  pickerHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  pickerTitle: {
    color: '#173D22',
    fontSize: 18,
    fontFamily: Fonts.rounded,
    fontWeight: '800',
  },
  pickerList: {
    gap: 10,
    paddingBottom: 12,
  },
  pickerItem: {
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#E5EEE5',
    padding: 14,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  pickerItemActive: {
    backgroundColor: '#E8F5E9',
    borderColor: '#1B5E20',
  },
  pickerName: {
    color: '#173D22',
    fontSize: 15,
    fontWeight: '800',
    fontFamily: Fonts.rounded,
  },
  pickerNameActive: {
    color: '#1B5E20',
  },
  pickerMeta: {
    color: '#6A8570',
    fontSize: 12,
  },
  pressed: {
    opacity: 0.82,
    transform: [{ scale: 0.99 }],
  },
});
